import React, { useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import * as Icons from "lucide-react";
import {
  AUDIENCES, BENEFITS, STEPS, ASSESSMENT, SERVICES, THERAPY_GOALS, CONSULTATION,
  OPTOMETRIST, ORTHOPTIST, WHO_WE_HELP, STATS, TESTIMONIALS, FAQS, IMAGES, AVAILABILITY, CREDENTIALS, FRAME_CATALOGUE_URL, FRAME_PAGES, INSIGHTS, VIDEO,
} from "@/config/content";
import { Reveal, SectionHeading, Counter } from "@/components/site/ui";
import { scrollToId } from "@/lib/scroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Ico = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`relative py-20 lg:py-28 ${className}`}>{children}</section>
);

/* ---------- Hero video feature (autoplay, muted, looping background style) ---------- */
export const VideoFeature = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  if (!VIDEO.src) return null;
  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next) { v.play().catch(() => {}); } // ensure playing when unmuting
  };
  return (
    <Section id="video" className="!py-10">
      <div className="max-site container-px">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] soft-shadow ring-1 ring-black/5 bg-brand-navy">
            <video
              ref={videoRef}
              className="w-full h-[320px] sm:h-[460px] lg:h-[560px] object-cover"
              src={VIDEO.src}
              poster={VIDEO.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              data-testid="hero-video"
            />
            {/* readability gradient */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-navy/85 via-brand-navy/25 to-transparent" />

            {/* "Watch our story" caption (top-left) */}
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full glass text-white/95 px-4 py-2 text-xs sm:text-sm font-semibold backdrop-blur-md ring-1 ring-white/20">
              <span className="grid place-items-center w-5 h-5 rounded-full bg-brand-teal"><Icons.Play className="w-3 h-3 text-white fill-white" /></span>
              Watch our story
            </div>

            {/* Mute / unmute toggle (top-right) */}
            <button onClick={toggleSound} data-testid="video-sound-toggle"
              aria-label={muted ? "Unmute video" : "Mute video"}
              className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full glass text-white backdrop-blur-md ring-1 ring-white/25 hover:bg-white/20 transition-colors focus-ring">
              {muted ? <Icons.VolumeX className="w-5 h-5" /> : <Icons.Volume2 className="w-5 h-5" />}
            </button>

            <div className="absolute inset-x-0 bottom-0 p-7 lg:p-12">
              <button onClick={() => scrollToId("contact")} data-testid="video-cta"
                className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-6 py-3.5 transition-colors focus-ring">
                Book a free school program <Icons.ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};

/* ---------- Audience strip ---------- */
export const AudienceStrip = () => (
  <Section id="audience" className="!py-14">
    <div className="max-site container-px">
      <Reveal><p className="text-center overline mb-8">Flexible vision programs designed for every stage of a child's education</p></Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {AUDIENCES.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.08}>
            <div data-cursor="hover" className="card-hover bg-white rounded-3xl p-6 border border-brand-navy/5 soft-shadow h-full">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-teal/10 text-brand-teal mb-4"><Ico name={a.icon} className="w-6 h-6" /></span>
              <h3 className="font-bold text-lg text-brand-navy">{a.title}</h3>
              <p className="text-sm text-brand-ink/70 mt-1">{a.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ---------- Sector strip (privacy-safe partners marquee) ---------- */
export const SectorStrip = () => {
  const reduce = useReducedMotion();
  const sectors = [
    { icon: "Landmark", label: "NSW Department of Education schools" },
    { icon: "Church", label: "Catholic schools" },
    { icon: "GraduationCap", label: "Independent schools" },
    { icon: "Baby", label: "Childcare centres & preschools" },
  ];
  const row = [...sectors, ...sectors, ...sectors];
  return (
    <section id="sectors" className="py-16 lg:py-20 bg-brand-bg overflow-hidden">
      <div className="max-site container-px">
        <Reveal>
          <p className="text-center overline mb-2">Trusted across every sector</p>
          <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-brand-navy max-w-2xl mx-auto">We partner with schools and centres of every kind</h2>
        </Reveal>
      </div>
      <div className="mt-10 relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-r from-brand-bg to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-l from-brand-bg to-transparent z-10" />
        <div className={`flex w-max ${reduce ? "" : "animate-marquee"}`}>
          {row.map((s, i) => (
            <div key={i} className="mx-3 flex items-center gap-3 bg-white rounded-2xl border border-brand-navy/5 soft-shadow px-6 py-4 whitespace-nowrap">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand-teal/10 text-brand-teal shrink-0"><Ico name={s.icon} className="w-5 h-5" /></span>
              <span className="font-heading font-bold text-brand-navy">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Free offer banner (attention grabber) ---------- */
export const FreeBanner = () => (
  <section id="free-offer" className="relative py-4">
    <div className="max-site container-px">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-brand-teal text-white px-7 py-10 lg:px-14 lg:py-14 soft-shadow">
          {/* organic shapes */}
          <div className="pointer-events-none absolute -top-16 -right-10 w-72 h-72 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-brand-navy/20 blur-2xl" />
          <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-amber text-brand-navy font-extrabold text-xs tracking-widest uppercase px-4 py-1.5">
                <Icons.Sparkles className="w-4 h-4" /> 100% Free
              </span>
              <h2 className="!text-white mt-4 text-3xl sm:text-4xl lg:text-6xl font-extrabold leading-[0.98] tracking-tight text-balance">
                Free Glasses for<br className="hidden sm:block" /> School Students
              </h2>
              <p className="mt-4 text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed">
                Plus free, comprehensive bulk-billed vision testing — delivered right at your school by our Orthoptists and Optometrists. Every student who needs glasses gets them, with no cost and no barriers.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={() => scrollToId("contact")} data-testid="free-banner-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-brand-teal font-bold px-7 py-3.5 hover:bg-white/90 transition-colors focus-ring">
                  Book a free school program <Icons.ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => scrollToId("how-it-works")} data-testid="free-banner-how"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-teal ring-1 ring-white/40 text-white font-semibold px-7 py-3.5 hover:bg-brand-tealdark transition-colors focus-ring">
                  How it works
                </button>
              </div>
            </div>
            <div className="hidden lg:grid place-items-center">
              <div className="grid place-items-center w-40 h-40 rounded-full bg-white/12 ring-1 ring-white/25">
                <Icons.Glasses className="w-24 h-24 text-white" strokeWidth={1.4} />
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- School vision testing ---------- */
export const SchoolTesting = () => (
  <Section id="school-testing">
    <div className="max-site container-px grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative">
          <div className="rounded-[2rem] overflow-hidden soft-shadow ring-1 ring-black/5">
            <img src={IMAGES.eyeTestChild} alt="Students being assessed during a Straight Sight vision-testing day at school" className="w-full h-[440px] object-cover" loading="lazy" />
          </div>
          <div className="absolute -bottom-6 left-6 right-6 lg:right-auto lg:w-80 bg-brand-navy text-white rounded-2xl p-5 soft-shadow">
            <p className="text-sm leading-relaxed"><Icons.Sparkles className="inline w-4 h-4 text-brand-amber mr-1" /> Good vision supports reading, concentration, confidence and classroom participation.</p>
          </div>
        </div>
      </Reveal>
      <div>
        <SectionHeading
          overline="Chapter 01 · School Vision Testing"
          title="Comprehensive Vision Testing at Your School"
          intro="Straight Sight is a leading provider of comprehensive school vision assessments. Our team brings professional eye care directly to local schools, making it easier for students to receive thorough vision testing in a familiar environment."
        />
        <Reveal delay={0.1}>
          <div className="mt-6 rounded-2xl border border-brand-teal/20 bg-brand-teal/6 p-5">
            <p className="font-semibold text-brand-navy flex items-start gap-2"><Icons.BadgeCheck className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" /> 100% free for schools and families — vision testing is fully bulk billed, and glasses are completely free.</p>
            <p className="text-sm text-brand-ink/75 mt-2">Assessments are bulk billed through Medicare, and any glasses a student needs are provided at no cost. Contact Straight Sight to arrange your school's program.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <button onClick={() => scrollToId("contact")} data-testid="school-testing-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-6 py-3 transition-colors focus-ring">
            Book your free school program <Icons.ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </div>
    </div>
  </Section>
);

/* ---------- Benefits ---------- */
export const Benefits = () => (
  <Section id="benefits" className="bg-white">
    <div className="max-site container-px">
      <SectionHeading center overline="Chapter 02 · Why Schools Choose Us" title="Everything a school needs, handled with care" />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {BENEFITS.map((b, i) => (
          <Reveal key={b.title} delay={(i % 3) * 0.08}>
            <div data-cursor="hover" className="group card-hover h-full bg-brand-bg rounded-3xl p-7 border border-brand-navy/5">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white text-brand-teal soft-shadow mb-5 group-hover:bg-brand-teal group-hover:text-white transition-colors"><Ico name={b.icon} className="w-6 h-6" /></span>
              <h3 className="font-bold text-xl text-brand-navy">{b.title}</h3>
              <p className="text-brand-ink/75 mt-2 leading-relaxed">{b.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ---------- How it works timeline ---------- */
export const HowItWorks = () => (
  <Section id="how-it-works">
    <div className="max-site container-px">
      <SectionHeading center overline="Chapter 03 · How the Program Works" title="A clear, coordinated six-step program" />
      <div className="mt-16 relative">
        <div className="hidden lg:block absolute top-[34px] left-0 right-0 h-0.5 bg-gradient-to-r from-brand-teal/20 via-brand-teal/50 to-brand-teal/20" />
        <div className="grid lg:grid-cols-6 gap-8 lg:gap-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="relative lg:text-center flex lg:block gap-4">
                <div className="shrink-0 relative z-10">
                  <span className="grid place-items-center w-[68px] h-[68px] rounded-2xl bg-brand-teal text-white font-heading font-extrabold text-xl soft-shadow lg:mx-auto">{s.n}</span>
                </div>
                <div className="lg:mt-5">
                  <h3 className="font-bold text-brand-navy">{s.title}</h3>
                  <p className="text-sm text-brand-ink/72 mt-1.5 leading-relaxed">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-14 text-center">
          <button onClick={() => scrollToId("contact")} data-testid="how-it-works-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold px-7 py-3.5 transition-colors focus-ring">
            Bring Straight Sight to Your School <Icons.ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ---------- Live availability ---------- */
export const Availability = () => {
  const styles = {
    open: { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700", ring: "border-emerald-200" },
    limited: { dot: "bg-brand-amber", badge: "bg-amber-50 text-amber-700", ring: "border-amber-200" },
    full: { dot: "bg-slate-400", badge: "bg-slate-100 text-slate-500", ring: "border-slate-200" },
  };
  return (
    <Section id="availability" className="bg-white">
      <div className="max-site container-px">
        <div className="rounded-[2rem] bg-brand-bg border border-brand-navy/5 soft-shadow p-8 lg:p-12">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-200 px-4 py-1.5 text-sm font-bold text-emerald-700">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                {AVAILABILITY.status}
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-brand-navy tracking-tight">Live program availability</h2>
              <p className="mt-3 text-brand-ink/75 max-w-xl leading-relaxed">{AVAILABILITY.intro}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                {AVAILABILITY.terms.map((t) => {
                  const s = styles[t.state] || styles.open;
                  return (
                    <div key={t.term} className={`bg-white rounded-2xl border ${s.ring} px-5 py-4 min-w-[160px]`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                        <span className="font-bold text-brand-navy">{t.term}</span>
                      </div>
                      <span className={`mt-2 inline-block text-xs font-semibold rounded-full px-2.5 py-1 ${s.badge}`}>{t.note}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:text-right">
              <button onClick={() => scrollToId("contact")} data-testid="availability-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-7 py-4 soft-shadow transition-colors focus-ring">
                <Icons.CalendarCheck className="w-5 h-5" /> Reserve your school's place
              </button>
              <p className="mt-3 text-xs text-brand-ink/55 lg:text-right">Places fill quickly — enquire to confirm your preferred term.</p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ---------- What the assessment covers (interactive) ---------- */
export const AssessmentCovers = () => {
  const [active, setActive] = useState(0);
  return (
    <Section id="assessment" className="bg-brand-navy text-white overflow-hidden">
      <div className="max-site container-px grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading light overline="Chapter 04 · What We Assess" title="What the assessment covers" intro="Hover or tap each area to learn what our Orthoptists and Optometrists examine." />
          <div className="mt-8 space-y-2">
            {ASSESSMENT.map((a, i) => (
              <button key={a.title} onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
                data-testid={`assessment-item-${i}`} data-cursor="hover"
                className={`w-full text-left rounded-2xl px-5 py-4 border transition-colors focus-ring ${active === i ? "bg-white/10 border-brand-teal" : "border-white/10 hover:border-white/25"}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className={`font-semibold ${active === i ? "text-white" : "text-white/80"}`}>{a.title}</span>
                  <Icons.ChevronRight className={`w-4 h-4 transition-transform ${active === i ? "rotate-90 text-brand-teal" : "text-white/40"}`} />
                </div>
                <motion.div initial={false} animate={{ height: active === i ? "auto" : 0, opacity: active === i ? 1 : 0 }} className="overflow-hidden">
                  <p className="text-sm text-white/70 pt-2 leading-relaxed">{a.body}</p>
                </motion.div>
              </button>
            ))}
          </div>
        </div>
        <div className="relative hidden lg:flex items-center justify-center">
          <div className="absolute w-80 h-80 rounded-full bg-brand-teal/20 blur-3xl" />
          <svg viewBox="0 0 400 300" className="relative w-full max-w-md" aria-hidden="true">
            <ellipse cx="200" cy="150" rx="150" ry="90" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
            <motion.circle cx="200" cy="150" r="58" fill="rgba(13,148,136,0.25)" stroke="#0D9488" strokeWidth="2"
              style={{ transformOrigin: "200px 150px" }}
              animate={{ scale: [0.96, 1.06, 0.96] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
            <circle cx="200" cy="150" r="24" fill="#0A192F" stroke="#38BDF8" strokeWidth="3" />
            <circle cx="192" cy="142" r="7" fill="white" opacity="0.9" />
          </svg>
          <div className="absolute bottom-2 glass text-brand-navy rounded-2xl px-5 py-3 soft-shadow">
            <p className="text-sm font-semibold">{ASSESSMENT[active].title}</p>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ---------- Services (expandable) ---------- */
export const Services = () => {
  const [open, setOpen] = useState(null);
  return (
    <Section id="services" className="bg-white">
      <div className="max-site container-px">
        <SectionHeading center overline="Chapter 05 · Our Services" title="Specialist eye care across every setting" />
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={s.title} delay={(i % 2) * 0.06}>
                <div data-cursor="hover" className={`card-hover rounded-3xl p-6 border transition-colors ${isOpen ? "bg-brand-teal/6 border-brand-teal/30" : "bg-brand-bg border-brand-navy/5"}`}>
                  <div className="flex items-start gap-4">
                    <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white text-brand-teal soft-shadow shrink-0"><Ico name={s.icon} className="w-6 h-6" /></span>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-brand-navy">{s.title}</h3>
                      <motion.div initial={false} animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }} className="overflow-hidden">
                        <p className="text-sm text-brand-ink/75 pt-2 leading-relaxed">{s.body}</p>
                      </motion.div>
                      <button onClick={() => setOpen(isOpen ? null : i)} data-testid={`service-toggle-${i}`}
                        className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal focus-ring">
                        {isOpen ? "Show less" : "Learn more"}
                        <Icons.ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

/* ---------- Orthoptic therapy + consultation + reviews ---------- */
export const Therapy = () => (
  <Section id="therapy">
    <div className="max-site container-px">
      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionHeading overline="Chapter 06 · Orthoptic Vision Therapy" title="Individualised Orthoptic Vision Therapy"
            intro="Orthoptic vision therapy is tailored to each patient's condition, visual abilities and treatment goals. The program assesses visual potential and develops selected visual skills, with the aim of helping the eyes work together as effectively and comfortably as possible." />
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {THERAPY_GOALS.map((g, i) => (
              <Reveal key={g} delay={i * 0.06}>
                <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-brand-navy/5 soft-shadow">
                  <span className="grid place-items-center w-9 h-9 rounded-xl bg-brand-teal/10 text-brand-teal shrink-0"><Icons.Check className="w-5 h-5" /></span>
                  <span className="text-sm font-medium text-brand-navy">{g}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-6 rounded-2xl bg-brand-sky/10 border border-brand-sky/25 p-5 text-sm text-brand-ink/80 leading-relaxed">
              Sessions may be scheduled weekly or fortnightly and are generally 45 minutes long. In-clinic sessions can be supported by prescribed home activities that reinforce the skills practised during appointments.
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="rounded-[2rem] overflow-hidden soft-shadow ring-1 ring-black/5">
            <img src={IMAGES.eyeTestChild2} alt="An Orthoptist engaging with a child in a friendly clinical environment" className="w-full h-[360px] object-cover" loading="lazy" />
          </div>
          {/* Consultation checklist */}
          <div className="mt-6 bg-white rounded-3xl p-7 border border-brand-navy/5 soft-shadow">
            <h3 className="font-bold text-xl text-brand-navy">What Happens During an Initial Consultation?</h3>
            <ul className="mt-4 space-y-2.5">
              {CONSULTATION.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-brand-ink/80">
                  <Icons.CheckCircle2 className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      {/* Reviews */}
      <Reveal delay={0.05}>
        <div className="mt-10 rounded-3xl bg-brand-navy text-white p-7 lg:p-9">
          <div className="flex items-center gap-2 mb-3"><Icons.RefreshCw className="w-5 h-5 text-brand-amber" /><h3 className="font-bold text-xl">Reviewing Progress</h3></div>
          <p className="text-white/80 leading-relaxed max-w-3xl">Once a tailored course of therapy is completed, the treating Orthoptist will recommend an appropriate review schedule. Reviews may take place after approximately four weeks, three months, six months and then annually, depending on the patient's individual needs. Reviews may assess progress, update home activities and provide guidance for maintaining healthy visual habits.</p>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ---------- Orthoptist vs Optometrist ---------- */
export const OrthoVsOpto = () => (
  <Section id="about" className="bg-white">
    <div className="max-site container-px">
      <SectionHeading center overline="Chapter 07 · About the Professions" title="Orthoptist and Optometrist — complementary expertise" />
      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {[{ t: "Optometrist", icon: "Glasses", list: OPTOMETRIST, accent: "bg-brand-sky/10 border-brand-sky/30" },
          { t: "Orthoptist", icon: "Eye", list: ORTHOPTIST, accent: "bg-brand-teal/8 border-brand-teal/30" }].map((c, i) => (
          <Reveal key={c.t} delay={i * 0.1}>
            <div className={`card-hover h-full rounded-3xl p-8 border ${c.accent}`}>
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white text-brand-teal soft-shadow"><Ico name={c.icon} className="w-6 h-6" /></span>
                <h3 className="font-extrabold text-2xl text-brand-navy">{c.t}</h3>
              </div>
              <ul className="space-y-2.5">
                {c.list.map((li) => (
                  <li key={li} className="flex items-start gap-3 text-brand-ink/80"><Icons.Dot className="w-5 h-5 text-brand-teal shrink-0" />{li}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-8 text-center text-brand-ink/80 max-w-3xl mx-auto text-lg">At Straight Sight, Orthoptists and Optometrists contribute complementary expertise to provide a more complete assessment of each student's visual needs.</p>
      </Reveal>
    </div>
  </Section>
);

/* ---------- Who we help (mosaic) ---------- */
export const WhoWeHelp = () => {
  const imgs = [IMAGES.groupGlasses, IMAGES.teensGroup, IMAGES.ssFitting, IMAGES.glassesRange];
  return (
    <Section id="who-we-help">
      <div className="max-site container-px">
        <SectionHeading center overline="Who We Help" title="Support for children and families at every stage" intro="We assess a wide range of visual needs. Not every learning difficulty is caused by a vision problem, but a thorough assessment helps identify where vision may be a contributing factor." />
        <div className="mt-14 grid lg:grid-cols-3 gap-6 items-start">
          <div className="grid grid-cols-2 gap-3">
            {imgs.map((src, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className={`rounded-2xl overflow-hidden soft-shadow ring-1 ring-black/5 ${i % 2 ? "mt-6" : ""}`}>
                  <img src={src} alt="Children and students supported by Straight Sight" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
            {WHO_WE_HELP.map((w, i) => (
              <Reveal key={w} delay={(i % 2) * 0.06}>
                <div data-cursor="hover" className="card-hover flex items-center gap-3 bg-white rounded-2xl p-5 border border-brand-navy/5 soft-shadow h-full">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal shrink-0"><Icons.HeartHandshake className="w-5 h-5" /></span>
                  <span className="font-medium text-brand-navy text-[15px]">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ---------- School reporting dashboard ---------- */
export const Reporting = () => (
  <Section id="reporting" className="bg-white">
    <div className="max-site container-px grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <SectionHeading overline="School Reporting" title="Clear reporting for parents, teachers and leadership" />
        <div className="mt-8 space-y-4">
          {[{ t: "Parent reports", d: "Parents receive the results of their child's comprehensive vision assessment and any recommended next steps." },
            { t: "Glasses information", d: "Where glasses are recommended, families receive information about frame selection and ordering — with glasses provided completely free." },
            { t: "Teacher summaries", d: "Teachers may receive a concise summary of relevant identified concerns, subject to appropriate consent and privacy processes." },
            { t: "School leadership reporting", d: "The principal or nominated representative may receive a de-identified summary showing the prevalence of identified concerns across participating grades or groups." }].map((r, i) => (
            <Reveal key={r.t} delay={i * 0.06}>
              <div className="flex gap-4">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-brand-teal/10 text-brand-teal shrink-0 font-bold text-sm">{i + 1}</span>
                <div><h3 className="font-bold text-brand-navy">{r.t}</h3><p className="text-sm text-brand-ink/75 mt-1">{r.d}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      {/* Simulated dashboard */}
      <Reveal delay={0.1}>
        <div className="rounded-[2rem] bg-brand-bg border border-brand-navy/5 soft-shadow p-6 lg:p-8">
          <div className="flex items-center justify-between mb-6">
            <div><p className="text-xs text-brand-ink/60">Sample program summary (illustrative)</p><p className="font-bold text-brand-navy text-lg">Whole-School Vision Report</p></div>
            <span className="text-xs font-semibold text-brand-teal bg-brand-teal/10 rounded-full px-3 py-1">De-identified</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[{ k: "Students seen", v: "—" }, { k: "Referrals", v: "—" }, { k: "Glasses ordered", v: "—" }].map((s) => (
              <div key={s.k} className="bg-white rounded-2xl p-4 text-center soft-shadow"><p className="text-2xl font-extrabold text-brand-navy">{s.v}</p><p className="text-[11px] text-brand-ink/60 mt-1">{s.k}</p></div>
            ))}
          </div>
          <div className="bg-white rounded-2xl p-5 soft-shadow">
            <p className="text-xs font-semibold text-brand-ink/60 mb-3">Identified concerns by area (illustrative)</p>
            {[["Refractive error", 62], ["Eye teaming", 34], ["Focusing", 28], ["Tracking", 21]].map(([label, pct], i) => (
              <div key={label} className="mb-3 last:mb-0">
                <div className="flex justify-between text-xs mb-1"><span className="text-brand-navy font-medium">{label}</span><span className="text-brand-ink/50">sample</span></div>
                <div className="h-2.5 rounded-full bg-brand-navy/8 overflow-hidden">
                  <motion.div className="h-full rounded-full bg-gradient-to-r from-brand-teal to-brand-sky" initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.2 + i * 0.1 }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-brand-ink/50 mt-4">Sample figures shown as placeholders. No real student names or identifiable health information are displayed.</p>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ---------- Glasses impact (Every pair of glasses means) ---------- */
export const GlassesImpact = () => {
  const items = ["Better reading", "Better learning", "Better confidence", "Better attendance", "Better futures"];
  return (
    <Section id="glasses-impact" className="bg-brand-bg">
      <div className="max-site container-px text-center">
        <SectionHeading center overline="Our Impact" title="Every pair of glasses means…" />
        <div className="mt-12 flex flex-wrap justify-center gap-3 sm:gap-4">
          {items.map((t, i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div data-cursor="hover" className="card-hover inline-flex items-center gap-3 bg-white rounded-full pl-3 pr-6 py-3 border border-brand-navy/5 soft-shadow">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-brand-teal text-white shrink-0"><Icons.Check className="w-5 h-5" strokeWidth={3} /></span>
                <span className="font-heading font-bold text-brand-navy text-base sm:text-lg">{t}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-brand-ink/70 max-w-2xl mx-auto">Clear vision helps children engage in the classroom and feel confident to take part — one free pair of glasses at a time.</p>
        </Reveal>
      </div>
    </Section>
  );
};

/* ---------- Program insights (anonymised analysis) ---------- */
export const ProgramInsights = () => (
  <Section id="insights" className="bg-white">
    <div className="max-site container-px grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <SectionHeading overline="Program Insights" title="What our assessments reveal" intro={INSIGHTS.intro} />
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-[2rem] bg-brand-teal text-white p-8 lg:p-10 soft-shadow">
            <p className="text-5xl lg:text-6xl font-extrabold tracking-tight">≈ {INSIGHTS.needGlassesRatio}</p>
            <p className="mt-3 text-white/90 text-lg font-semibold">students assessed were found to need glasses</p>
            <p className="mt-2 text-white/75 text-sm">Many receiving their first-ever pair — at no cost to their family.</p>
          </div>
        </Reveal>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {INSIGHTS.years.map((y, i) => (
          <Reveal key={y.year} delay={i * 0.1}>
            <div data-cursor="hover" className="card-hover h-full bg-brand-bg rounded-3xl p-7 border border-brand-navy/5 soft-shadow">
              <p className="text-sm font-bold text-brand-teal">{y.year}</p>
              <div className="mt-4 space-y-4">
                <div><p className="text-3xl font-extrabold text-brand-navy">{y.tested.toLocaleString()}</p><p className="text-xs text-brand-ink/60">Students assessed</p></div>
                <div><p className="text-3xl font-extrabold text-brand-navy">{y.schools}</p><p className="text-xs text-brand-ink/60">Schools tested</p></div>
                <div><p className="text-3xl font-extrabold text-brand-navy">{y.glasses.toLocaleString()}</p><p className="text-xs text-brand-ink/60">Students needing glasses</p></div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ---------- Choose your free frames ---------- */
export const FrameRange = () => {
  const [lb, setLb] = useState(null);
  return (
    <Section id="frames" className="bg-white">
      <div className="max-site container-px">
        <SectionHeading center overline="Free Frames" title="Choose your free frames" intro="Every student who needs glasses can choose from a great range — for kids, teens and adults — in a variety of shapes and colours, at no cost. Frames and lenses are provided through vision foundations including the Essilor Vision Foundation and Opticare." />
        <div className="mt-7 flex flex-wrap justify-center gap-2.5">
          {["Kids", "Adolescents", "Adults", "Plastic & metal", "Multiple colours"].map((t) => (
            <span key={t} className="inline-flex items-center gap-2 rounded-full bg-brand-bg border border-brand-navy/8 px-4 py-2 text-sm font-semibold text-brand-navy">
              <Icons.Check className="w-4 h-4 text-brand-teal" /> {t}
            </span>
          ))}
        </div>
        <div className="mt-10 flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4">
          {FRAME_PAGES.map((f, i) => (
            <button key={i} onClick={() => setLb(f)} data-testid={`frame-page-${i}`} data-cursor="hover"
              className="snap-start shrink-0 w-[280px] sm:w-[360px] rounded-2xl overflow-hidden soft-shadow ring-1 ring-black/5 bg-white card-hover">
              <img src={f.src} alt={`Free glasses frame range — ${f.label}`} className="w-full h-auto" loading="lazy" />
              <span className="flex items-center justify-center gap-2 text-sm font-bold text-brand-navy py-3"><Icons.Glasses className="w-4 h-4 text-brand-teal" /> {f.label}</span>
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-brand-ink/50">Swipe to browse · tap a page to enlarge</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={() => scrollToId("contact")} data-testid="frame-book-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-6 py-3.5 transition-colors focus-ring">
            Book a school program <Icons.ArrowRight className="w-4 h-4" />
          </button>
          <a href={FRAME_CATALOGUE_URL} target="_blank" rel="noopener noreferrer" data-testid="frame-catalogue-link"
            className="inline-flex items-center gap-2 rounded-full bg-white text-brand-navy font-semibold px-6 py-3.5 border border-brand-navy/10 soft-shadow hover:border-brand-teal/40 transition-colors focus-ring">
            <Icons.BookOpen className="w-4 h-4 text-brand-teal" /> View full catalogue (PDF)
          </a>
        </div>
      </div>
      {lb && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-brand-navy/85 backdrop-blur-sm p-6" onClick={() => setLb(null)} data-testid="frame-lightbox">
          <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-4xl w-full">
            <img src={lb.src} alt={`Free glasses frame range — ${lb.label}`} className="w-full rounded-2xl bg-white" />
            <p className="text-white/80 text-sm mt-3 text-center">{lb.label} · free frame range</p>
            <button onClick={() => setLb(null)} className="absolute -top-3 -right-3 grid place-items-center w-10 h-10 rounded-full bg-white text-brand-navy soft-shadow focus-ring" aria-label="Close"><Icons.X className="w-5 h-5" /></button>
          </motion.div>
        </div>
      )}
    </Section>
  );
};

/* ---------- Stats bar (prominent, near top) ---------- */
export const StatsBar = () => (
  <section id="stats" className="relative pt-2 pb-6">
    <div className="max-site container-px">
      <Reveal>
        <div className="rounded-[2rem] bg-white soft-shadow ring-1 ring-brand-navy/5 px-6 py-8 lg:px-12 lg:py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
          {STATS.map((s, i) => (
            <div key={s.label} className="text-center lg:border-r last:border-r-0 border-brand-navy/8">
              <p className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-teal tracking-tight"><Counter value={s.value} placeholder={s.placeholder} suffix={s.suffix} /></p>
              <p className="mt-1.5 text-brand-ink/70 text-xs sm:text-sm font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- Impact (happy students outcome) ---------- */
export const Impact = () => (
  <Section id="impact" className="bg-brand-navy text-white overflow-hidden">
    <div className="max-site container-px grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <div className="relative rounded-[2rem] overflow-hidden soft-shadow ring-1 ring-white/10">
          <img src={IMAGES.groupGlasses} alt="A happy group of primary-school students giving a thumbs up after receiving their new glasses" className="w-full h-[380px] object-cover" loading="lazy" />
          <span className="absolute bottom-4 left-4 glass text-brand-navy rounded-full px-4 py-1.5 text-xs font-bold">Happy students, clearer vision</span>
        </div>
      </Reveal>
      <div>
        <SectionHeading light overline="Our Impact" title="Real outcomes for the students who need us most" />
        <p className="mt-6 text-white/80 leading-relaxed">
          By bringing free assessments and free glasses directly into schools, we remove the cost and time barriers that can stop children from getting the eyewear they need — and we follow through, delivering completed glasses back to the school so no student misses out.
        </p>
        <Reveal delay={0.1}>
          <button onClick={() => scrollToId("contact")} data-testid="impact-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-7 py-3.5 transition-colors focus-ring">
            Book a free program <Icons.ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </div>
    </div>
  </Section>
);

/* ---------- Testimonials carousel ---------- */
export const Testimonials = () => {
  const [i, setI] = useState(0);
  const t = TESTIMONIALS[i];
  const move = (d) => setI((p) => (p + d + TESTIMONIALS.length) % TESTIMONIALS.length);
  return (
    <Section id="testimonials">
      <div className="max-site container-px">
        <SectionHeading center overline="Testimonials" title="Voices from school communities" intro="Kind words from the school communities we partner with. Additional quotes will be added as they are approved." />
        <div className="mt-12 max-w-3xl mx-auto">
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="relative bg-white rounded-[2rem] p-8 lg:p-12 border border-brand-navy/5 soft-shadow text-center">
            <Icons.Quote className="w-10 h-10 text-brand-teal/25 mx-auto mb-4" />
            <p className="text-lg lg:text-xl text-brand-navy font-medium leading-relaxed">"{t.quote}"</p>
            <div className="mt-6"><p className="font-bold text-brand-navy">{t.name}</p><p className="text-sm text-brand-teal">{t.role}</p></div>
            {t.placeholder && <span className="absolute top-6 right-6 text-[10px] font-bold uppercase tracking-wide text-brand-amber bg-brand-amber/10 rounded-full px-2.5 py-1">Placeholder</span>}
          </motion.div>
          <div className="flex items-center justify-center gap-3 mt-6">
            <button onClick={() => move(-1)} data-testid="testimonial-prev" className="grid place-items-center w-11 h-11 rounded-full bg-white border border-brand-navy/10 soft-shadow hover:border-brand-teal focus-ring" aria-label="Previous testimonial"><Icons.ChevronLeft className="w-5 h-5 text-brand-navy" /></button>
            <div className="flex gap-1.5">{TESTIMONIALS.map((_, k) => <span key={k} className={`w-2 h-2 rounded-full transition-colors ${k === i ? "bg-brand-teal" : "bg-brand-navy/15"}`} />)}</div>
            <button onClick={() => move(1)} data-testid="testimonial-next" className="grid place-items-center w-11 h-11 rounded-full bg-white border border-brand-navy/10 soft-shadow hover:border-brand-teal focus-ring" aria-label="Next testimonial"><Icons.ChevronRight className="w-5 h-5 text-brand-navy" /></button>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ---------- Gallery with lightbox ---------- */
export const Gallery = () => {
  const [lb, setLb] = useState(null);
  const imgs = [
    { src: IMAGES.groupGlasses, cap: "Happy students with their new glasses" },
    { src: IMAGES.teensTable, cap: "High-school students collecting their glasses" },
    { src: IMAGES.teensGroup, cap: "Students proud of their new glasses" },
    { src: IMAGES.ssFitting, cap: "A student fitted with new glasses" },
    { src: IMAGES.ssClassroom, cap: "School assessment day" },
    { src: IMAGES.ssTesting, cap: "High-school vision testing" },
    { src: IMAGES.glassesRange, cap: "A colourful range of frames to choose from" },
    { src: IMAGES.glassesDelivery3, cap: "Glasses delivery day at school" },
    { src: IMAGES.ssDelivery, cap: "Glasses delivered to school" },
    { src: IMAGES.pyjamaDay, cap: "A vibrant school community we've supported" },
    { src: IMAGES.thanksNote, cap: "A thank-you note from a participating school" },
    { src: IMAGES.glassesCollage, cap: "Students celebrating their new glasses" },
  ];
  return (
    <Section id="gallery" className="bg-white">
      <div className="max-site container-px">
        <SectionHeading center overline="Gallery" title="A look inside our programs" intro="Real moments from Straight Sight school vision programs — assessments, glasses fittings and delivery day." />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {imgs.map((im, k) => (
            <Reveal key={k} delay={(k % 3) * 0.06}>
              <button onClick={() => setLb(im)} data-testid={`gallery-item-${k}`} data-cursor="hover" className="group relative block w-full rounded-2xl overflow-hidden soft-shadow ring-1 ring-black/5 focus-ring">
                <img src={im.src} alt={im.cap} className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <span className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/30 transition-colors" />
                <span className="absolute bottom-3 left-3 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity">{im.cap}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      {lb && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-brand-navy/85 backdrop-blur-sm p-6" onClick={() => setLb(null)} data-testid="lightbox">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-3xl w-full">
            <img src={lb.src} alt={lb.cap} className="w-full rounded-2xl" />
            <p className="text-white/80 text-sm mt-3 text-center">{lb.cap}</p>
            <button onClick={() => setLb(null)} className="absolute -top-3 -right-3 grid place-items-center w-10 h-10 rounded-full bg-white text-brand-navy soft-shadow focus-ring" aria-label="Close"><Icons.X className="w-5 h-5" /></button>
          </motion.div>
        </div>
      )}
    </Section>
  );
};

/* ---------- Marquee ---------- */
export const Marquee = () => {
  const items = ["School vision testing", "Paediatric orthoptics", "Amblyopia support", "Vision therapy", "Glasses assistance", "Parent reporting", "Childcare screening"];
  const reduce = useReducedMotion();
  return (
    <div className="py-10 bg-brand-teal overflow-hidden" aria-hidden="true">
      <div className={`flex whitespace-nowrap ${reduce ? "" : "animate-marquee"}`}>
        {[...items, ...items].map((t, i) => (
          <span key={i} className="mx-8 text-2xl lg:text-3xl font-heading font-extrabold text-white/90 flex items-center gap-8">
            {t} <Icons.Eye className="w-6 h-6 text-white/50" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- FAQ ---------- */
export const FAQ = () => (
  <Section id="faqs">
    <div className="max-site container-px grid lg:grid-cols-3 gap-12">
      <div className="lg:col-span-1">
        <SectionHeading overline="FAQs" title="Answers for schools & families" intro="Can't find what you need? Contact Straight Sight directly." />
      </div>
      <div className="lg:col-span-2">
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="bg-white rounded-2xl border border-brand-navy/5 soft-shadow px-5">
              <AccordionTrigger className="text-left font-semibold text-brand-navy hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-brand-ink/75 leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </Section>
);

/* ---------- How vision affects learning (education) ---------- */
export const VisionLearning = () => (
  <Section id="vision-learning" className="bg-white">
    <div className="max-site container-px grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <SectionHeading overline="Vision & Learning" title="How vision affects learning"
          intro="Clear, comfortable vision does more than help a child see the board. When the eyes struggle to focus, work together or track smoothly across a page, reading and concentration can take far more effort. These illustrations show how everyday print may appear to a child experiencing common vision difficulties — the kind of concerns a comprehensive assessment can help identify." />
        <div className="mt-8 space-y-3">
          {[["Focus", "Eye focusing", "When focusing is difficult, print can appear blurred or drift in and out of clarity."],
            ["Combine", "Eye teaming", "When the eyes don't team well, letters and words can overlap or appear doubled."],
            ["Move", "Eye tracking", "When tracking is inaccurate, the eyes lose their place and skip or re-read words."]].map(([icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="flex gap-4 items-start bg-brand-bg rounded-2xl p-4 border border-brand-navy/5">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal shrink-0"><Ico name={icon} className="w-5 h-5" /></span>
                <div><h3 className="font-bold text-brand-navy">{t}</h3><p className="text-sm text-brand-ink/75 mt-0.5">{d}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-sm text-brand-ink/75 rounded-2xl border border-brand-amber/25 bg-brand-amber/8 p-4 flex items-start gap-2">
            <Icons.Info className="w-5 h-5 text-brand-amber shrink-0 mt-0.5" />
            A vision difficulty is only one possible factor in a child's learning. Not every learning challenge is related to vision — but a thorough assessment helps identify whether vision is playing a part.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="space-y-4">
          <div className="rounded-[1.5rem] bg-brand-bg border border-brand-navy/5 soft-shadow p-4">
            <img src={IMAGES.eduChart} alt="Illustration comparing how print may appear with eye-focusing, eye-teaming, eye-movement and perceptual difficulties" className="w-full rounded-xl" loading="lazy" />
            <p className="text-xs text-brand-ink/55 mt-2 text-center">Illustrative examples of how print may appear to some children</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["eduTeaming", "Eye teaming"], ["eduTracking", "Eye tracking"]].map(([k, label]) => (
              <div key={k} className="rounded-[1.5rem] bg-brand-navy p-3">
                <img src={IMAGES[k]} alt={`Animated demonstration of an ${label.toLowerCase()} difficulty`} className="w-full h-32 object-cover rounded-xl bg-white" loading="lazy" />
                <p className="text-xs text-white/70 mt-2 text-center">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ---------- Credentials (registrations & memberships) ---------- */
export const Credentials = () => (
  <Section id="credentials" className="bg-brand-bg">
    <div className="max-site container-px">
      <SectionHeading center overline="Trust & Safety" title="Registered, insured and qualified" intro="Straight Sight meets the professional, safety and insurance requirements for working with children in schools and childcare settings." />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {CREDENTIALS.map((c, i) => (
          <Reveal key={c.title} delay={(i % 4) * 0.08}>
            <div data-cursor="hover" className="card-hover h-full bg-white rounded-3xl p-7 border border-brand-navy/5 soft-shadow text-center">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-brand-teal/10 text-brand-teal mx-auto mb-5"><Ico name={c.icon} className="w-7 h-7" /></span>
              <h3 className="font-bold text-brand-navy">{c.title}</h3>
              <p className="text-sm text-brand-ink/72 mt-2 leading-relaxed">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ---------- Final CTA ---------- */
export const FinalCTA = () => (
  <section id="final-cta" className="relative py-24 lg:py-32 overflow-hidden">
    <img src={IMAGES.ctaSchool} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-brand-navy/82" />
    <div className="relative max-site container-px text-center">
      <Reveal>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white text-balance max-w-3xl mx-auto leading-tight">Bring Clearer Vision to Your School Community</h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-5 text-white/80 text-lg max-w-2xl mx-auto">Talk to Straight Sight about organising a comprehensive vision assessment program for your students.</p>
      </Reveal>
      <Reveal delay={0.14}>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <button onClick={() => scrollToId("contact")} data-testid="final-cta-request" className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-7 py-3.5 shadow-xl transition-colors focus-ring">Request a School Program <Icons.ArrowRight className="w-4 h-4" /></button>
          <a href="tel:+61400630986" data-testid="final-cta-call" className="inline-flex items-center gap-2 rounded-full bg-white text-brand-navy font-semibold px-7 py-3.5 hover:bg-white/90 transition-colors focus-ring"><Icons.Phone className="w-4 h-4 text-brand-teal" /> Call Straight Sight</a>
        </div>
      </Reveal>
    </div>
  </section>
);
